import React, { useEffect, useMemo, useRef, useState } from "react";

export const HOOD_CAREER_LEVELS = [
  { elo: 0, en: "Beginner", pl: "Początek", es: "Principiante", reward: "First Steps" },
  { elo: 800, en: "Starter", pl: "Starter", es: "Inicio", reward: "Opening Basics Chest" },
  { elo: 1200, en: "Squire", pl: "Giermek", es: "Escudero", reward: "Tactical Eye Badge" },
  { elo: 1600, en: "Guardian", pl: "Strażnik", es: "Guardián", reward: "Club Fighter Chest" },
  { elo: 2000, en: "Castle", pl: "Zamek", es: "Castillo", reward: "Deep Analysis Badge" },
  { elo: 2400, en: "Master", pl: "Mistrz", es: "Maestro", reward: "Elite Preparation Chest" },
  { elo: 3000, en: "Super GM", pl: "Super GM", es: "Super GM", reward: "Chesshood Legend Crown" },
];

function tr(lang, row) {
  return row?.[lang] || row?.en || "";
}

function clampRating(value) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.min(3000, number)) : 0;
}

function lifecycleLabel(days, lang) {
  const value = Math.max(0, Math.round(Number(days) || 0));
  if (lang === "pl") {
    if (value <= 1) return "Pierwsza wizyta";
    if (value <= 7) return "Nowy użytkownik";
    if (value < 30) return "Powracający użytkownik";
    return "Stały użytkownik";
  }
  if (lang === "es") {
    if (value <= 1) return "Primera visita";
    if (value <= 7) return "Usuario nuevo";
    if (value < 30) return "Usuario recurrente";
    return "Usuario habitual";
  }
  if (value <= 1) return "First visit";
  if (value <= 7) return "New user";
  if (value < 30) return "Returning user";
  return "Regular user";
}

export function crossedMilestones(fromRating, toRating) {
  const from = clampRating(fromRating);
  const to = clampRating(toRating);
  if (to <= from) return [];
  return HOOD_CAREER_LEVELS.filter((row) => row.elo > 0 && row.elo > from && row.elo <= to);
}

export default function HoodCareerPath({
  rating = 0,
  lang = "en",
  accountAgeDays = null,
  userId = "guest",
  qa = false,
  compact = false,
}) {
  const value = clampRating(rating);
  const storageKey = `hood:career:last-rating:v1:${userId || "guest"}:${qa ? "qa" : "real"}`;
  const previousRef = useRef(value);
  const [reward, setReward] = useState(null);
  const progress = (value / 3000) * 100;

  const currentLevel = useMemo(() => {
    let current = HOOD_CAREER_LEVELS[0];
    for (const row of HOOD_CAREER_LEVELS) {
      if (value >= row.elo) current = row;
    }
    return current;
  }, [value]);

  const nextLevel = useMemo(
    () => HOOD_CAREER_LEVELS.find((row) => row.elo > value) || null,
    [value]
  );

  useEffect(() => {
    let previous = previousRef.current;
    try {
      const rawSaved = window.localStorage.getItem(storageKey);
      if (rawSaved !== null && rawSaved !== "") {
        const saved = Number(rawSaved);
        if (Number.isFinite(saved)) previous = clampRating(saved);
      }
    } catch {}

    const crossed = crossedMilestones(previous, value);
    previousRef.current = value;
    try { window.localStorage.setItem(storageKey, String(value)); } catch {}

    if (!crossed.length) return undefined;

    const last = crossed[crossed.length - 1];
    setReward({
      ...last,
      count: crossed.length,
      all: crossed,
    });

    const timer = window.setTimeout(() => setReward(null), 4300);
    return () => window.clearTimeout(timer);
  }, [storageKey, value]);

  const copy = lang === "pl"
    ? {
        title: "Twoja droga szachowa",
        rating: "Ranking",
        next: "Do następnego poziomu",
        unlocked: "ODBLOCKOWANO NAGRODĘ",
        crossed: "Przekroczone progi",
        day: "Dzień",
      }
    : lang === "es"
      ? {
          title: "Tu carrera de ajedrez",
          rating: "Rating",
          next: "Hasta el próximo nivel",
          unlocked: "RECOMPENSA DESBLOQUEADA",
          crossed: "Niveles superados",
          day: "Día",
        }
      : {
          title: "Your chess career",
          rating: "Rating",
          next: "To next level",
          unlocked: "REWARD UNLOCKED",
          crossed: "Milestones crossed",
          day: "Day",
        };

  return <>
    <section style={{
      position: "relative",
      background: "linear-gradient(180deg,rgba(12,35,61,.96),rgba(8,22,42,.96))",
      border: "1px solid rgba(125,211,252,.28)",
      borderRadius: compact ? 16 : 20,
      padding: compact ? "12px 12px 16px" : "15px 16px 20px",
      overflow: "hidden",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", marginBottom: compact ? 12 : 16 }}>
        <div>
          <div style={{ color: "#7dd3fc", fontSize: 10, fontWeight: 900, letterSpacing: ".11em" }}>{copy.title.toUpperCase()}</div>
          <div style={{ marginTop: 4, fontWeight: 900, fontSize: compact ? 16 : 19 }}>
            {tr(lang, currentLevel)} · {Math.round(value)}
          </div>
        </div>
        {accountAgeDays !== null && accountAgeDays !== undefined && (
          <div style={{ textAlign: "right" }}>
            <div style={{ color: "#afc2d8", fontSize: 10, fontWeight: 900 }}>{copy.day} {Math.round(Number(accountAgeDays) || 0)}</div>
            <div style={{ color: qa ? "#ffd400" : "#dbeafe", fontSize: 11, marginTop: 2 }}>{lifecycleLabel(accountAgeDays, lang)}</div>
          </div>
        )}
      </div>

      <div style={{
        position: "relative",
        height: compact ? 180 : 330,
        margin: compact ? "2px 4px 4px" : "4px 6px 8px",
      }}>
        {(() => {
          const points = compact
            ? [
                { x: 8, y: 86 },
                { x: 34, y: 76 },
                { x: 18, y: 61 },
                { x: 50, y: 49 },
                { x: 31, y: 34 },
                { x: 69, y: 21 },
                { x: 91, y: 9 },
              ]
            : [
                { x: 8, y: 88 },
                { x: 36, y: 78 },
                { x: 16, y: 64 },
                { x: 53, y: 51 },
                { x: 29, y: 36 },
                { x: 70, y: 22 },
                { x: 92, y: 8 },
              ];

          const pathD = compact
            ? "M 8 86 C 19 80, 30 82, 34 76 S 12 68, 18 61 S 58 55, 50 49 S 22 40, 31 34 S 78 27, 69 21 S 84 12, 91 9"
            : "M 8 88 C 19 82, 32 84, 36 78 S 8 71, 16 64 S 62 58, 53 51 S 18 43, 29 36 S 79 29, 70 22 S 84 12, 92 8";

          let segment = 0;
          for (let i = 0; i < HOOD_CAREER_LEVELS.length - 1; i += 1) {
            if (value >= HOOD_CAREER_LEVELS[i + 1].elo) segment = i + 1;
          }
          const lastIndex = HOOD_CAREER_LEVELS.length - 1;
          let marker = points[Math.min(segment, lastIndex)];
          if (segment < lastIndex) {
            const from = HOOD_CAREER_LEVELS[segment].elo;
            const to = HOOD_CAREER_LEVELS[segment + 1].elo;
            const local = Math.max(0, Math.min(1, (value - from) / Math.max(1, to - from)));
            const a = points[segment];
            const b = points[segment + 1];
            marker = {
              x: a.x + ((b.x - a.x) * local),
              y: a.y + ((b.y - a.y) * local),
            };
          }

          return <>
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}
            >
              <path
                d={pathD}
                pathLength="100"
                fill="none"
                stroke="rgba(255,255,255,.13)"
                strokeWidth={compact ? "3.2" : "4"}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d={pathD}
                pathLength="100"
                fill="none"
                stroke="url(#careerGold)"
                strokeWidth={compact ? "3.2" : "4"}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={`${progress} 100`}
                style={{ transition: "stroke-dasharray .45s ease" }}
              />
              <defs>
                <linearGradient id="careerGold" x1="0" x2="1">
                  <stop offset="0%" stopColor="#ffd400" />
                  <stop offset="100%" stopColor="#ff9f1c" />
                </linearGradient>
              </defs>
            </svg>

            {HOOD_CAREER_LEVELS.map((row, index) => {
              const p = points[index];
              const done = value >= row.elo;
              const labelSide = index % 2 === 0 ? "left" : "right";
              return <div key={row.elo} style={{
                position: "absolute",
                left: `${p.x}%`,
                top: `${p.y}%`,
                transform: "translate(-50%,-50%)",
                zIndex: 4,
              }}>
                <div style={{
                  width: compact ? 24 : 30,
                  height: compact ? 24 : 30,
                  borderRadius: 999,
                  background: done ? "#ffd400" : "#17344c",
                  border: done ? "3px solid #fff2a8" : "3px solid #587089",
                  boxShadow: done ? "0 0 16px rgba(255,212,0,.55)" : "none",
                }} />
                <div style={{
                  position: "absolute",
                  top: compact ? 26 : 34,
                  ...(labelSide === "left" ? { right: compact ? 6 : 8 } : { left: compact ? 6 : 8 }),
                  width: compact ? 62 : 86,
                  textAlign: labelSide === "left" ? "right" : "left",
                  color: done ? "#f8fafc" : "#7890a7",
                  fontSize: compact ? 8 : 11,
                  fontWeight: 900,
                  lineHeight: 1.12,
                  textShadow: "0 1px 2px rgba(0,0,0,.55)",
                }}>
                  <div style={{ color: done ? "#ffd400" : "inherit" }}>{row.elo}</div>
                  <div style={{ marginTop: 2 }}>{tr(lang, row)}</div>
                </div>
              </div>;
            })}

            <div style={{
              position: "absolute",
              left: `${marker.x}%`,
              top: `${marker.y}%`,
              transform: "translate(-50%,-72%)",
              width: compact ? 52 : 72,
              height: compact ? 66 : 90,
              zIndex: 7,
              transition: "left .45s ease, top .45s ease",
              filter: "drop-shadow(0 6px 10px rgba(0,0,0,.5))",
            }}>
              <img src="/hood.png" alt="Hood career marker" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            </div>
          </>;
        })()}
      </div>

      {nextLevel && !compact && (
        <div style={{ color: "#afc2d8", fontSize: 11, textAlign: "center", marginTop: -3 }}>
          {copy.next}: <strong style={{ color: "#ffd400" }}>{nextLevel.elo - Math.round(value)}</strong> · {tr(lang, nextLevel)}
        </div>
      )}
    </section>

    {reward && <div style={{
      position: "fixed",
      inset: 0,
      zIndex: 10050,
      background: "rgba(2,8,23,.76)",
      display: "grid",
      placeItems: "center",
      padding: 20,
      animation: "hoodRewardFade .18s ease-out",
    }} onClick={() => setReward(null)}>
      <style>{`
        @keyframes hoodRewardPop{0%{transform:scale(.72) translateY(20px);opacity:0}65%{transform:scale(1.06) translateY(-4px);opacity:1}100%{transform:scale(1) translateY(0);opacity:1}}
        @keyframes hoodRewardGlow{0%,100%{filter:drop-shadow(0 0 8px #ffd40066)}50%{filter:drop-shadow(0 0 26px #ffd400dd)}}
        @keyframes hoodRewardFade{from{opacity:0}to{opacity:1}}
      `}</style>
      <div onClick={(event) => event.stopPropagation()} style={{
        width: "min(92vw,390px)",
        textAlign: "center",
        background: "linear-gradient(180deg,#163a5a,#0a1f38)",
        border: "2px solid #ffd400",
        borderRadius: 26,
        padding: "24px 20px 20px",
        color: "#fff",
        boxShadow: "0 25px 80px rgba(0,0,0,.55)",
        animation: "hoodRewardPop .42s cubic-bezier(.2,.85,.25,1.15)",
      }}>
        <div style={{ fontSize: 58, animation: "hoodRewardGlow 1.5s ease-in-out infinite" }}>🎁</div>
        <div style={{ color: "#ffd400", fontSize: 11, fontWeight: 950, letterSpacing: ".13em", marginTop: 5 }}>{copy.unlocked}</div>
        <h2 style={{ margin: "8px 0 4px", fontSize: 27 }}>{tr(lang, reward)} · {reward.elo}</h2>
        <div style={{ color: "#dbeafe", fontWeight: 850, fontSize: 16 }}>{reward.reward}</div>
        {reward.count > 1 && <div style={{ color: "#afc2d8", marginTop: 8, fontSize: 12 }}>
          {copy.crossed}: {reward.all.map((row) => row.elo).join(" · ")}
        </div>}
        <button type="button" onClick={() => setReward(null)} style={{
          marginTop: 17,
          border: 0,
          borderRadius: 14,
          background: "#ffd400",
          color: "#102b40",
          fontWeight: 950,
          minHeight: 44,
          padding: "9px 18px",
        }}>✓</button>
      </div>
    </div>}
  </>;
}

export { lifecycleLabel };
